import React, { useRef, useState, useEffect } from 'react';
import { isProceduralBackground, getProceduralKey } from '../proceduralBackgrounds';
import ProceduralBackground from '../ProceduralBackground';
import ProceduralForeground from '../ProceduralForeground';

const ABS_FILL = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' };
const DEFAULT_PROCEDURAL_GAMEPLAY_SPEED = 3;

function getProceduralGameplaySpeed(key) {
  switch (key) {
    case 'skyClouds':
      return 6;
    default:
      return DEFAULT_PROCEDURAL_GAMEPLAY_SPEED;
  }
}

/** Compute cover-style tile size so one tile = same crop as background-size:cover + center */
function getCoverTileSize(containerW, containerH, imgW, imgH) {
  if (!containerW || !containerH || !imgW || !imgH) return null;
  const scale = Math.max(containerW / imgW, containerH / imgH);
  return { width: imgW * scale, height: imgH * scale };
}

/** Determine repeat axis from direction */
function getScrollAxis(direction) {
  if (direction === 'left' || direction === 'right') return { repeat: 'repeat-x', axis: 'x' };
  if (direction === 'up' || direction === 'down') return { repeat: 'repeat-y', axis: 'y' };
  return null;
}

// Unique ID for injected keyframes — only inject once
let parallaxStyleInjected = false;
function ensureParallaxStyles() {
  if (parallaxStyleInjected) return;
  parallaxStyleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes sf-parallax-x { from { background-position-x: var(--sf-bg-start-x); } to { background-position-x: var(--sf-bg-end-x); } }
    @keyframes sf-parallax-y { from { background-position-y: var(--sf-bg-start-y); } to { background-position-y: var(--sf-bg-end-y); } }
  `;
  document.head.appendChild(style);
}

/** Scrolling image background with parallax — uses CSS animation (compositor thread, no rAF) */
const ParallaxImageBackground = ({ image, containerRef, direction, isAnimating, speedScale = 1 }) => {
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imgNaturalSize, setImgNaturalSize] = useState(null);

  // Inject keyframes once
  useEffect(() => { ensureParallaxStyles(); }, []);

  // Measure container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  // Load image to get natural dimensions
  useEffect(() => {
    if (!image) { setImgNaturalSize(null); return; }
    const img = new Image();
    img.onload = () => setImgNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => setImgNaturalSize(null);
    img.src = image;
  }, [image]);

  const cw = containerSize.width;
  const ch = containerSize.height;
  const tile = imgNaturalSize && cw && ch
    ? getCoverTileSize(cw, ch, imgNaturalSize.width, imgNaturalSize.height)
    : null;

  const scrollInfo = getScrollAxis(direction);
  if (!image || !scrollInfo) return null;

  const isReverse = direction === 'right' || direction === 'down';
  const isHorizontal = scrollInfo.axis === 'x';

  // CSS animation: scroll one full tile width/height, then loop seamlessly
  const tileSpan = tile ? (isHorizontal ? tile.width : tile.height) : 1000;
  const SCROLL_SPEED = 60 * (speedScale || 1); // px/sec; speedScale lets per-game callers (e.g. Flap & Fly) dial parallax up
  const duration = tileSpan / SCROLL_SPEED; // seconds for one full tile scroll

  // Start/end positions for one full tile cycle
  // Horizontal scrollers: start from left edge so uploaded backgrounds begin at their left side
  const baseX = tile ? (isHorizontal ? 0 : (cw - tile.width) / 2) : 0;
  const baseY = tile ? (!isHorizontal ? 0 : (ch - tile.height) / 2) : 0;

  const cssVars = {};
  let animationName = 'none';

  // Always compute animation params when tile is ready so pausing preserves position
  if (tile) {
    if (isHorizontal) {
      const startX = baseX;
      const endX = isReverse ? baseX + tileSpan : baseX - tileSpan;
      cssVars['--sf-bg-start-x'] = `${startX}px`;
      cssVars['--sf-bg-end-x'] = `${endX}px`;
      animationName = 'sf-parallax-x';
    } else {
      const startY = baseY;
      const endY = isReverse ? baseY + tileSpan : baseY - tileSpan;
      cssVars['--sf-bg-start-y'] = `${startY}px`;
      cssVars['--sf-bg-end-y'] = `${endY}px`;
      animationName = 'sf-parallax-y';
    }
  }

  const initialPos = tile
    ? `${baseX}px ${baseY}px`
    : 'center center';

  return (
    <div
      style={{
        ...ABS_FILL,
        zIndex: 0,
        backgroundImage: `url(${image})`,
        backgroundPosition: initialPos,
        backgroundRepeat: scrollInfo.repeat,
        backgroundSize: tile ? `${tile.width}px ${tile.height}px` : 'cover',
        backfaceVisibility: 'hidden',
        willChange: isAnimating ? 'background-position' : 'auto',
        transform: 'translateZ(0)',
        animation: tile && animationName !== 'none' ? `${animationName} ${duration}s linear infinite` : 'none',
        animationPlayState: isAnimating ? 'running' : 'paused',
        ...cssVars,
      }}
    />
  );
};

/**
 * GameBackgroundLayer — Unified background + foreground for all game types.
 *
 * Always mounted (across start, live, complete states). Renders:
 *   - Background layer (procedural canvas / scrolling image / static image / solid color) at zIndex 0
 *   - Foreground wisp layer (procedural only, if background has a foreground layer) at zIndex 6
 *
 * @param {string|null} backgroundImage   - URL, procedural:<key>, or null
 * @param {string}      backgroundColor   - Hex fallback color
 * @param {string|null} direction          - 'left'|'right'|'up'|'down'|null (from gameTypes)
 * @param {boolean}     isParallaxActive   - true only during live gameplay
 * @param {boolean}     staticBackground   - user toggle to disable image parallax
 * @param {number}      parallaxSpeedScale - multiplier applied during gameplay so per-game callers can compensate for slow foreground action (e.g. Flap & Fly's drifting pipes). Defaults to 1.
 */
const GameBackgroundLayer = React.memo(({
  backgroundImage,
  backgroundColor,
  direction = null,
  isParallaxActive = false,
  staticBackground = false,
  parallaxSpeedScale = 1,
}) => {
  const containerRef = useRef(null);
  const defaultColor = backgroundColor || '#87CEEB';
  const isProcedural = isProceduralBackground(backgroundImage);
  const proceduralKey = isProcedural ? getProceduralKey(backgroundImage) : null;
  const hasDirection = direction != null;

  // Determine if image parallax should be active
  const imageParallaxActive = isParallaxActive && !staticBackground && hasDirection && !isProcedural && !!backgroundImage;

  // Procedural speed boost during gameplay (× per-game scale so callers can dial it up)
  const proceduralSpeedMultiplier = isParallaxActive && hasDirection
    ? getProceduralGameplaySpeed(proceduralKey) * (parallaxSpeedScale || 1)
    : 1.0;
  const proceduralAnimating = !staticBackground;

  // Direction stays constant — avoids jarring snap when gameplay starts
  const proceduralDirection = direction || null;

  return (
    <>
      <div ref={containerRef} style={{ ...ABS_FILL, overflow: 'hidden', zIndex: 0 }}>
        {/* Background layer */}
        {isProcedural ? (
          <ProceduralBackground
            rendererKey={proceduralKey}
            style={{ zIndex: 0 }}
            direction={proceduralDirection}
            speedMultiplier={proceduralSpeedMultiplier}
            isAnimating={proceduralAnimating}
          />
        ) : backgroundImage ? (
          imageParallaxActive ? (
            <ParallaxImageBackground
              image={backgroundImage}
              containerRef={containerRef}
              direction={direction}
              isAnimating={true}
              speedScale={parallaxSpeedScale}
            />
          ) : (
            <div
              style={{
                ...ABS_FILL,
                zIndex: 0,
                backgroundImage: `url(${backgroundImage})`,
                backgroundPosition: (direction === 'left' || direction === 'right') ? 'left center'
                  : (direction === 'up' || direction === 'down') ? 'center top'
                  : 'center center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              }}
            />
          )
        ) : (
          <div style={{ ...ABS_FILL, zIndex: 0, backgroundColor: defaultColor }} />
        )}
      </div>

      {/* Foreground wisp layer — rendered as sibling (not child) so it sits above game content */}
      {isProcedural && (
        <ProceduralForeground
          rendererKey={proceduralKey}
        />
      )}
    </>
  );
});

GameBackgroundLayer.displayName = 'GameBackgroundLayer';

export default GameBackgroundLayer;
